import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Flex, Text, Button, Table, Thead, Tbody, Tr, Th, Td, Badge, HStack,
  SimpleGrid, Spinner, Alert, AlertIcon, Input, Select, FormControl, FormLabel,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
  VStack, Switch, useToast, useDisclosure, Divider,
} from '@chakra-ui/react';
import { FiArrowLeft, FiRefreshCw, FiEdit2, FiSave } from 'react-icons/fi';
import api from '../api';

const fmt = (n) => '£' + parseFloat(n || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function PensionDashboard({ onBack }) {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editEmp, setEditEmp] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const editModal = useDisclosure();
  const toast = useToast();

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/salary');
      setEmployees(data);
    } catch(e) {}
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openEdit = (emp) => {
    setEditEmp(emp);
    setEditForm({
      pension_employee_pct: emp.pension_employee_pct || 5,
      pension_employer_pct: emp.pension_employer_pct || 3,
      pension_provider: emp.pension_provider || '',
      pension_ref: emp.pension_ref || '',
      auto_enrolled: emp.auto_enrolled !== false,
    });
    editModal.onOpen();
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      await api.put(`/salary/${editEmp.id}`, editForm);
      toast({ title: 'Pension updated', status: 'success', duration: 1500 });
      fetchAll();
      editModal.onClose();
    } catch(e) { toast({ title: 'Failed', status: 'error', duration: 2000 }); }
    setSaving(false);
  };

  const activeEmps = employees.filter(e => e.is_active && e.auto_enrolled !== false);
  const totalEEPension = activeEmps.reduce((a, e) => {
    const qual = Math.max(0, Math.min(parseFloat(e.gross_annual || 0), 50270) - 6240);
    return a + qual * (parseFloat(e.pension_employee_pct || 5) / 100);
  }, 0);
  const totalERPension = activeEmps.reduce((a, e) => {
    const qual = Math.max(0, Math.min(parseFloat(e.gross_annual || 0), 50270) - 6240);
    return a + qual * (parseFloat(e.pension_employer_pct || 3) / 100);
  }, 0);

  return (
    <Box p={4} maxW="1200px" mx="auto">
      <Flex align="center" justify="space-between" mb={4}>
        <HStack>
          <Button size="sm" variant="ghost" leftIcon={<FiArrowLeft />} onClick={onBack}>Back</Button>
          <Text fontWeight="700" fontSize="16px">Pension Management</Text>
          <Badge colorScheme="purple">{activeEmps.length} enrolled</Badge>
        </HStack>
        <Button size="sm" leftIcon={<FiRefreshCw />} variant="ghost" onClick={fetchAll}>Refresh</Button>
      </Flex>

      {/* Summary */}
      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3} mb={6}>
        {[
          ['Enrolled Employees', activeEmps.length, 'purple'],
          ['Opted Out', employees.filter(e => e.is_active && e.auto_enrolled === false).length, 'gray'],
          ['Annual EE Contributions', fmt(totalEEPension), 'blue'],
          ['Annual ER Contributions', fmt(totalERPension), 'green'],
        ].map(([label, val, color]) => (
          <Box key={label} p={3} border="1px" borderColor="gray.200" borderRadius="lg">
            <Text fontSize="9px" color="gray.500" textTransform="uppercase" fontWeight="600">{label}</Text>
            <Text fontSize="16px" fontWeight="800" color={`${color}.600`}>{val}</Text>
          </Box>
        ))}
      </SimpleGrid>

      <Alert status="info" mb={4} fontSize="11px" borderRadius="md">
        <AlertIcon />
        UK auto-enrolment minimum contributions 2024/25: 5% employee (on qualifying earnings), 3% employer.
        Qualifying earnings band: £6,240–£50,270. Always verify with The Pensions Regulator.
      </Alert>

      {loading ? <Flex justify="center" py={8}><Spinner /></Flex> : (
        <Box border="1px" borderColor="gray.200" borderRadius="md" overflow="hidden">
          <Table size="sm">
            <Thead bg="gray.50">
              <Tr>
                <Th fontSize="9px">Employee</Th>
                <Th fontSize="9px">Status</Th>
                <Th fontSize="9px">Provider</Th>
                <Th fontSize="9px">Ref</Th>
                <Th fontSize="9px" isNumeric>EE %</Th>
                <Th fontSize="9px" isNumeric>ER %</Th>
                <Th fontSize="9px" isNumeric>Annual EE</Th>
                <Th fontSize="9px" isNumeric>Annual ER</Th>
                <Th fontSize="9px" isNumeric>Total/yr</Th>
                <Th />
              </Tr>
            </Thead>
            <Tbody>
              {employees.filter(e => e.is_active).length === 0 && (
                <Tr><Td colSpan={10} textAlign="center" py={8} color="gray.400">No active employees.</Td></Tr>
              )}
              {employees.filter(e => e.is_active).map(e => {
                const gross = parseFloat(e.gross_annual || 0);
                const qual = Math.max(0, Math.min(gross, 50270) - 6240);
                const eePct = parseFloat(e.pension_employee_pct || 5);
                const erPct = parseFloat(e.pension_employer_pct || 3);
                const annualEE = qual * (eePct / 100);
                const annualER = qual * (erPct / 100);
                const enrolled = e.auto_enrolled !== false;
                return (
                  <Tr key={e.id} opacity={enrolled ? 1 : 0.5}>
                    <Td>
                      <Text fontSize="12px" fontWeight="600">{e.employee_name || `${e.first_name} ${e.last_name}`}</Text>
                    </Td>
                    <Td>
                      <Badge colorScheme={enrolled ? 'green' : 'gray'} fontSize="8px">
                        {enrolled ? 'Enrolled' : 'Opted Out'}
                      </Badge>
                    </Td>
                    <Td fontSize="11px">{e.pension_provider || '—'}</Td>
                    <Td fontSize="10px" color="gray.500">{e.pension_ref || '—'}</Td>
                    <Td isNumeric fontSize="11px">{enrolled ? `${eePct}%` : '—'}</Td>
                    <Td isNumeric fontSize="11px">{enrolled ? `${erPct}%` : '—'}</Td>
                    <Td isNumeric fontSize="11px" color="blue.600">{enrolled ? fmt(annualEE) : '—'}</Td>
                    <Td isNumeric fontSize="11px" color="green.600">{enrolled ? fmt(annualER) : '—'}</Td>
                    <Td isNumeric fontSize="12px" fontWeight="700">{enrolled ? fmt(annualEE + annualER) : '—'}</Td>
                    <Td>
                      <Button size="xs" leftIcon={<FiEdit2 />} variant="ghost" onClick={() => openEdit(e)}>Edit</Button>
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </Box>
      )}

      {/* Edit Modal */}
      <Modal isOpen={editModal.isOpen} onClose={editModal.onClose} size="md">
        <ModalOverlay /><ModalContent>
          <ModalHeader fontSize="md">Edit Pension — {editEmp?.employee_name}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={3}>
              <HStack align="center" w="100%">
                <Switch size="sm" colorScheme="green" isChecked={!!editForm.auto_enrolled}
                  onChange={e => setEditForm(p => ({ ...p, auto_enrolled: e.target.checked }))} />
                <Text fontSize="12px" fontWeight="600">Auto-enrolled in workplace pension</Text>
              </HStack>
              {editForm.auto_enrolled && (
                <>
                  <HStack w="100%">
                    <FormControl>
                      <FormLabel fontSize="11px">Employee Contribution %</FormLabel>
                      <Input size="sm" type="number" step="0.5" min="0" max="100"
                        value={editForm.pension_employee_pct}
                        onChange={e => setEditForm(p => ({ ...p, pension_employee_pct: parseFloat(e.target.value) }))} />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="11px">Employer Contribution %</FormLabel>
                      <Input size="sm" type="number" step="0.5" min="0" max="100"
                        value={editForm.pension_employer_pct}
                        onChange={e => setEditForm(p => ({ ...p, pension_employer_pct: parseFloat(e.target.value) }))} />
                    </FormControl>
                  </HStack>
                  <FormControl>
                    <FormLabel fontSize="11px">Pension Provider</FormLabel>
                    <Input size="sm" value={editForm.pension_provider}
                      onChange={e => setEditForm(p => ({ ...p, pension_provider: e.target.value }))}
                      placeholder="e.g. NEST, The People's Pension, Royal London" />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="11px">Employee Pension Reference</FormLabel>
                    <Input size="sm" value={editForm.pension_ref}
                      onChange={e => setEditForm(p => ({ ...p, pension_ref: e.target.value }))} />
                  </FormControl>
                </>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button size="sm" variant="ghost" mr={2} onClick={editModal.onClose}>Cancel</Button>
            <Button size="sm" colorScheme="purple" leftIcon={<FiSave />} onClick={saveEdit} isLoading={saving}>Save</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
